/**
 * SD UGC Slider Single - Thumbnail video navigation
 */

class UgcSliderSingle extends HTMLElement {
  constructor () {
    super()
    this.videos = this.querySelectorAll('.sd-ugc-single__video')
    this.thumbs = this.querySelectorAll('.sd-ugc-single__thumb')
    this.thumbsContainer = this.querySelector('.sd-ugc-single__thumbnails')
    this.mediaColumn = this.querySelector('.sd-ugc-single__media')
    this.activeIndex = 0
    this.thumbGap = 8
    this.resizeTimeout = null
  }

  connectedCallback () {
    this.bindEvents()
    this.calculateThumbsWidth()
    window.addEventListener('resize', () => this.handleResize())
  }

  handleResize () {
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => this.calculateThumbsWidth(), 100)
  }

  getThumbSize () {
    // 64px on desktop (750px+), 60px on mobile
    return window.innerWidth >= 750 ? 64 : 60
  }

  calculateThumbsWidth () {
    if (!this.thumbsContainer || !this.mediaColumn) return

    const thumbCount = this.thumbs.length
    if (thumbCount === 0) return

    const thumbSize = this.getThumbSize()

    // Get the max available width from the media column
    const maxWidth = this.mediaColumn.offsetWidth

    // Calculate how many full thumbnails fit
    // Formula: (thumbSize * n) + (gap * (n - 1)) <= maxWidth
    // Solving for n: n <= (maxWidth + gap) / (thumbSize + gap)
    const maxThumbs = Math.floor((maxWidth + this.thumbGap) / (thumbSize + this.thumbGap))
    const visibleThumbs = Math.min(thumbCount, maxThumbs)

    // Calculate exact width to fit visible thumbnails
    const exactWidth = (visibleThumbs * thumbSize) + ((visibleThumbs - 1) * this.thumbGap)

    this.thumbsContainer.style.setProperty('--ugc-single-thumbs-width', `${exactWidth}px`)
  }

  bindEvents () {
    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.switchVideo(index))
    })

    // Handle video clicks/taps and play events
    this.videos.forEach((videoContainer) => {
      const video = videoContainer.querySelector('video')
      if (video) {
        // Track touch to differentiate tap from scroll
        let touchStartY = 0
        let touchStartTime = 0

        video.addEventListener('touchstart', (e) => {
          touchStartY = e.touches[0].clientY
          touchStartTime = Date.now()
        }, { passive: true })

        video.addEventListener('touchend', (e) => {
          const touchEndY = e.changedTouches[0].clientY
          const touchDuration = Date.now() - touchStartTime
          const touchDistance = Math.abs(touchEndY - touchStartY)

          // Only toggle if it's a quick tap (not a scroll)
          if (touchDuration < 300 && touchDistance < 10) {
            const rect = video.getBoundingClientRect()
            const tapY = touchEndY - rect.top
            const isControlsArea = tapY > rect.height - 50

            if (!isControlsArea) {
              e.preventDefault()
              this.toggleVideo(video)
            }
          }
        })

        // Desktop click handler
        video.addEventListener('click', (e) => {
          // Check if click is on the controls area (bottom ~40px)
          const rect = video.getBoundingClientRect()
          const clickY = e.clientY - rect.top
          const isControlsArea = clickY > rect.height - 40

          if (!isControlsArea) {
            e.preventDefault()
            this.toggleVideo(video)
          }
        })

        // Pause other videos when one starts playing
        video.addEventListener('play', () => this.pauseOtherVideos(video))
      }
    })
  }

  toggleVideo (video) {
    if (video.paused) {
      video.play()
    } else {
      video.pause()
    }
  }

  switchVideo (index) {
    if (index === this.activeIndex) return

    // Pause current video
    const currentVideo = this.videos[this.activeIndex]?.querySelector('video')
    if (currentVideo) {
      currentVideo.pause()
    }

    // Update active states
    this.videos.forEach((v, i) => {
      v.classList.toggle('is-active', i === index)
    })

    this.thumbs.forEach((t, i) => {
      t.classList.toggle('is-active', i === index)
    })

    this.activeIndex = index

    // Scroll selected thumbnail into view if needed
    this.scrollThumbIntoView(index)
  }

  scrollThumbIntoView (index) {
    if (!this.thumbsContainer || !this.thumbs[index]) return

    const thumbSize = this.getThumbSize()
    const currentScroll = this.thumbsContainer.scrollLeft
    const containerWidth = this.thumbsContainer.offsetWidth

    // Calculate the position of the clicked thumbnail
    const thumbStart = index * (thumbSize + this.thumbGap)
    const thumbEnd = thumbStart + thumbSize

    // Check if thumbnail is at or near the left edge and there's content to the left
    const isAtLeftEdge = thumbStart <= currentScroll + thumbSize
    const hasContentToLeft = index > 0 && currentScroll > 0

    // Check if thumbnail is at or near the right edge and there's content to the right
    const isAtRightEdge = thumbEnd >= currentScroll + containerWidth - thumbSize
    const hasContentToRight = index < this.thumbs.length - 1

    if (isAtLeftEdge && hasContentToLeft) {
      // Scroll left to show previous thumbnail(s)
      const newScroll = Math.max(0, thumbStart - thumbSize - this.thumbGap)
      this.thumbsContainer.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      })
    } else if (isAtRightEdge && hasContentToRight) {
      // Scroll right to show next thumbnail(s)
      this.thumbsContainer.scrollTo({
        left: thumbStart,
        behavior: 'smooth'
      })
    }
  }

  pauseOtherVideos (currentVideo) {
    this.videos.forEach((videoContainer) => {
      const video = videoContainer.querySelector('video')
      if (video && video !== currentVideo) {
        video.pause()
      }
    })
  }
}

customElements.define('ugc-slider-single', UgcSliderSingle)
